import React, { useEffect, useState } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { collection, query, getDocs, orderBy, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Package, ShoppingBag, Users, ClipboardList,
  Plus, LogOut, TrendingUp, AlertCircle, Tag, RotateCcw, BarChart2, Truck
} from "lucide-react";
import { formatPrice } from "../utils/format";
import toast from "react-hot-toast";

// ── Admin Sidebar Layout ──────────────────────────────────────────────────────
export function AdminLayout() {
  const { profile, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [returnCount, setReturnCount] = useState(0);

  useEffect(() => {
    getDocs(query(collection(db, "product_submissions"), where("status", "==", "pending")))
      .then(s => setPendingCount(s.size));
    getDocs(query(collection(db, "returns"), where("status", "==", "pending")))
      .then(s => setReturnCount(s.size));
  }, []);

  const links = [
    { to: "/admin",              label: "Dashboard",   icon: LayoutDashboard,  exact: true },
    { to: "/admin/analytics",    label: "Analytics",   icon: BarChart2 },
    { to: "/admin/products",     label: "Products",    icon: Package },
    { to: "/admin/products/add", label: "Add Product", icon: Plus },
    { to: "/admin/orders",       label: "Orders",      icon: ShoppingBag },
    { to: "/admin/shipping",     label: "Shipping & Pickup", icon: Truck },
    { to: "/admin/submissions",  label: "Submissions", icon: ClipboardList,  badge: pendingCount },
    { to: "/admin/returns",      label: "Returns",     icon: RotateCcw,      badge: returnCount },
    { to: "/admin/coupons",      label: "Coupons",     icon: Tag },
    { to: "/admin/users",        label: "Users",       icon: Users },
  ];

  const isActive = (to, exact) =>
    exact ? location.pathname === to : location.pathname.startsWith(to) && !(exact === false && location.pathname === "/admin");

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className="admin-sidebar" style={{ display: "flex", flexDirection: "column", background: "linear-gradient(180deg, #0D1B2A 0%, #162436 100%)", borderRight: "1px solid rgba(255,255,255,0.05)" }}>
        <div style={{ padding: "28px 24px", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img src="/images/Afriportstore.png" alt="Afriport Logo" style={{ height: 44, width: "auto", objectFit: "contain" }} />
          </Link>
          <div style={{ marginTop: 24, padding: "12px 14px", background: "rgba(255,255,255,0.04)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.05)" }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "var(--brand)", marginBottom: 4 }}>ADMIN PANEL</p>
            <p style={{ fontSize: 14, color: "#F8FAFC", fontWeight: 600 }}>{profile?.fullName}</p>
          </div>
        </div>

        <nav style={{ padding: "16px 12px", flex: 1, overflowY: "auto" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {links.map(({ to, label, icon: Icon, exact, badge }) => {
              const active = isActive(to, exact);
              return (
                <Link key={to} to={to}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", borderRadius: 10,
                    color: active ? "#fff" : "#94A3B8",
                    background: active ? "linear-gradient(90deg, rgba(232,71,26,0.15) 0%, transparent 100%)" : "transparent",
                    fontSize: 14, fontWeight: active ? 600 : 500, textDecoration: "none",
                    borderLeft: active ? "3px solid var(--brand)" : "3px solid transparent",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.color = "#E2E8F0"; }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#94A3B8"; } }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Icon size={18} style={{ color: active ? "var(--brand)" : "inherit" }} /> {label}
                  </div>
                  {badge > 0 && (
                    <span style={{ background: "var(--brand)", color: "#fff", fontSize: 11, fontWeight: 700, minWidth: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{badge}</span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        <div style={{ padding: "16px 12px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", color: "#94A3B8", fontSize: 14, fontWeight: 500, borderRadius: 10, textDecoration: "none", marginBottom: 8, transition: "background 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            ← Back to Store
          </Link>
          <button onClick={async () => { await logout(); navigate("/"); toast.success("Logged out"); }}
            style={{ display: "flex", alignItems: "center", gap: 10, color: "#FCA5A5", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "10px 14px", borderRadius: 10, width: "100%", transition: "all 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(239,68,68,0.15)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(239,68,68,0.1)"}>
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="admin-main" style={{ background: "var(--surface)", minHeight: "100vh" }}>
        <Outlet />
      </main>
    </div>
  );
}

// ── Admin Dashboard ───────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color = "var(--brand)", sub }) {
  return (
    <div className="card" style={{ padding: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start", background: "#fff", border: "1px solid var(--border)", transition: "transform 0.2s, box-shadow 0.2s" }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "var(--shadow-sm)"; }}>
      <div>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</p>
        <p style={{ fontFamily: "var(--font-head)", fontSize: 32, fontWeight: 800, color: "var(--text)", lineHeight: 1.1 }}>{value}</p>
        {sub && <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 6, fontWeight: 500 }}>{sub}</p>}
      </div>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={24} color={color} />
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { profile } = useAuth();
  const [stats, setStats] = useState({ products: 0, orders: 0, users: 0, revenue: 0, pendingSubs: 0, pendingReturns: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [products, orders, users, subs, returns_] = await Promise.all([
          getDocs(query(collection(db, "products"), where("status", "==", "active"))),
          getDocs(query(collection(db, "orders"), orderBy("createdAt", "desc"))),
          getDocs(collection(db, "users")),
          getDocs(query(collection(db, "product_submissions"), where("status", "==", "pending"))),
          getDocs(query(collection(db, "returns"), where("status", "==", "pending"))),
        ]);
        const orderDocs = orders.docs.map(d => ({ id: d.id, ...d.data() }));
        const revenue = orderDocs.reduce((s, o) => s + (o.total || 0), 0);
        setStats({ products: products.size, orders: orderDocs.length, users: users.size, revenue, pendingSubs: subs.size, pendingReturns: returns_.size });
        setRecentOrders(orderDocs.slice(0, 8));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  const STATUS_BADGE = { confirmed: "badge-info", processing: "badge-warning", shipped: "badge-default", delivered: "badge-success", cancelled: "badge-danger" };

  return (
    <div style={{ padding: "clamp(20px, 4vw, 40px) clamp(14px, 3vw, 48px)", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>Dashboard Overview</h1>
        <p style={{ color: "var(--text-muted)", fontSize: 15, marginTop: 4 }}>Welcome back, {profile?.fullName?.split(" ")[0] || "Admin"}</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 24, marginBottom: 40 }}>
        <StatCard label="Total Revenue" value={formatPrice(stats.revenue)} icon={TrendingUp} color="#10B981" />
        <StatCard label="Total Orders" value={stats.orders} icon={ShoppingBag} color="#3B82F6" />
        <StatCard label="Total Products" value={stats.products} icon={Package} color="#F5A623" />
        <StatCard label="Registered Users" value={stats.users} icon={Users} color="#8B5CF6" />
        
        {stats.pendingSubs > 0 && (
          <StatCard label="Pending Submissions" value={stats.pendingSubs} icon={AlertCircle} color="#E8471A" sub="Requires your approval" />
        )}
      </div>

      <div className="card" style={{ background: "#fff", border: "1px solid var(--border)", overflow: "hidden" }}>
        <div style={{ padding: "24px 28px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 700 }}>Recent Orders</h2>
          <Link to="/admin/orders" style={{ fontSize: 14, color: "var(--brand)", fontWeight: 600, textDecoration: "none" }}>View All →</Link>
        </div>
        
        {loading ? <div style={{ padding: 40, textAlign: "center" }}><div className="loader"><div className="spinner" /></div></div> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
                  <th style={{ padding: "16px 28px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Order ID</th>
                  <th style={{ padding: "16px 28px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Customer</th>
                  <th style={{ padding: "16px 28px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Date</th>
                  <th style={{ padding: "16px 28px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total</th>
                  <th style={{ padding: "16px 28px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.length === 0 ? (
                  <tr><td colSpan="5" style={{ padding: "40px 28px", textAlign: "center", color: "var(--text-muted)" }}>No orders yet.</td></tr>
                ) : recentOrders.map(order => (
                  <tr key={order.id} style={{ borderBottom: "1px solid var(--border)", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "16px 28px", fontSize: 14, fontFamily: "monospace", fontWeight: 500 }}>{order.id.slice(0, 8).toUpperCase()}</td>
                    <td style={{ padding: "16px 28px", fontSize: 14, fontWeight: 500 }}>{order.userName || order.userId?.slice(0,8)}</td>
                    <td style={{ padding: "16px 28px", fontSize: 14, color: "var(--text-muted)" }}>{order.createdAt?.toDate().toLocaleDateString()}</td>
                    <td style={{ padding: "16px 28px", fontSize: 14, fontWeight: 600 }}>{formatPrice(order.total || 0)}</td>
                    <td style={{ padding: "16px 28px" }}>
                      <span className={`badge ${STATUS_BADGE[order.status] || "badge-default"}`} style={{ textTransform: "capitalize", padding: "4px 10px", fontSize: 12, fontWeight: 600 }}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
